import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { Hammer, Send, CheckCircle2, Camera, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  urgent: "bg-red-600 text-white", high: "bg-amber-500 text-white",
  normal: "bg-stone-100 text-stone-600 border border-stone-200", low: "bg-stone-100 text-stone-400 border border-stone-200",
};

export function ReqImg({ rid, pid, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API}/work-requests/${rid}/photos/${pid}/image`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
        if (res.ok) { revoked = URL.createObjectURL(await res.blob()); setUrl(revoked); }
      } catch (e) { console.warn("ReqImg load failed", e); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [rid, pid]);
  if (!url) return <div className={`bg-stone-100 animate-pulse ${className}`} />;
  return <img src={url} alt="request" className={className} />;
}

export default function PortalRequests() {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState("normal");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const load = () => api.get("/portal/work-requests").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = (list) => {
    const picked = [...list].map((file) => ({ file, preview: URL.createObjectURL(file), key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}` }));
    setFiles((s) => [...s, ...picked]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const { data: req } = await api.post("/portal/work-requests", { title, details, priority });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        try {
          await api.post(`/portal/work-requests/${req.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) {
          toast.error(`Photo ${f.file.name || ""} failed to upload`);
        }
      }
      setTitle(""); setDetails(""); setPriority("normal");
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      toast.success("Work request submitted");
      load();
    } catch (err) {
      toast.error("Could not submit request");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="portal-requests">
      <form onSubmit={submit} className="bg-white border border-stone-200 p-5 space-y-3" data-testid="work-request-form">
        <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-2"><Hammer size={14} /> Request Work</div>
        <input data-testid="work-request-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="What work do you need done? *" required
          className="w-full h-11 border border-stone-300 px-3 text-sm" />
        <textarea data-testid="work-request-details" value={details} onChange={(e) => setDetails(e.target.value)}
          placeholder="Details — blocks, timing, anything the crew should know" rows={3}
          className="w-full border border-stone-300 p-3 text-sm" />

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid="work-request-camera-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
          data-testid="work-request-photo-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="work-request-take-photo" onClick={() => cameraRef.current?.click()}
            className="h-10 px-4 border border-stone-300 font-bold uppercase text-xs flex items-center gap-2">
            <Camera size={14} /> Take Photo
          </button>
          <button type="button" data-testid="work-request-add-photos" onClick={() => galleryRef.current?.click()}
            className="h-10 px-4 border border-stone-300 font-bold uppercase text-xs flex items-center gap-2">
            <ImagePlus size={14} /> Add Photos
          </button>
        </div>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="work-request-previews">
            {files.map((f) => (
              <div key={f.key} className="relative">
                <img src={f.preview} alt="preview" className="h-20 w-20 object-cover border border-stone-200" />
                <button type="button" onClick={() => { URL.revokeObjectURL(f.preview); setFiles((s) => s.filter((x) => x.key !== f.key)); }}
                  className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white flex items-center justify-center"><X size={12} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <select data-testid="work-request-priority" value={priority} onChange={(e) => setPriority(e.target.value)}
            className="h-11 border border-stone-300 px-2 text-sm">
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <button data-testid="work-request-submit" disabled={saving}
            className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-50">
            <Send size={14} /> {saving ? "Sending…" : "Submit Request"}
          </button>
        </div>
      </form>

      <div className="space-y-2" data-testid="work-request-list">
        {(rows || []).map((r) => (
          <div key={r.id} data-testid={`work-request-${r.id}`}
            className={`bg-white border p-4 ${r.status === "complete" ? "border-stone-200 opacity-70" : "border-stone-200"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-bold">{r.title}</div>
                {r.details && <div className="text-sm text-stone-500 mt-0.5 whitespace-pre-wrap">{r.details}</div>}
                <div className="text-[10px] text-stone-400 mt-1">{(r.created_at || "").slice(0, 10)}{r.status === "complete" ? ` · completed ${(r.completed_at || "").slice(0, 10)} by ${r.completed_by}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-1 ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.normal}`}>{r.priority}</span>
                {r.status === "complete"
                  ? <span className="text-[10px] font-bold uppercase px-2 py-1 bg-[#7C9673] text-white flex items-center gap-1"><CheckCircle2 size={11} /> Complete</span>
                  : r.status === "approved"
                    ? <span className="text-[10px] font-bold uppercase px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300">Approved · In Progress</span>
                    : <span className="text-[10px] font-bold uppercase px-2 py-1 bg-[#1F3D34] text-white">Open</span>}
              </div>
            </div>
            {(r.photos || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {r.photos.map((p) => <ReqImg key={p.id} rid={r.id} pid={p.id} className="h-24 w-24 object-cover border border-stone-200" />)}
              </div>
            )}
          </div>
        ))}
        {rows && rows.length === 0 && (
          <div className="bg-white border border-stone-200 p-8 text-center text-stone-400 font-bold uppercase text-sm" data-testid="work-requests-empty">
            No requests yet — submit your first one above
          </div>
        )}
      </div>
    </div>
  );
}
